import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';

@Entity()
export class MailProcessingStats extends AdornisEntity {
  static override _class = 'MailProcessingStats';

  @Field(() => Int)
  sumExtractions?: number;

  @Field(() => Int)
  sumCorrect?: number;

  @Field(() => Int)
  sumIncorrect?: number;

  @Field(() => Int)
  sumNotEvaluated?: number;

  @Field(() => Int)
  sumCorrectAfterCorrection?: number;

  @Field(() => Int)
  sumCorrectWithAdjustment?: number;

  @Field(() => Int)
  sumUnsolvable?: number;

  constructor(doc: PartialEntityData<MailProcessingStats>) {
    super(doc);
  }
}

/** The counter shape both the global stats and the per-user rows share. */
export interface EvaluationCounters {
  sumExtractions?: number;
  sumCorrect?: number;
  sumIncorrect?: number;
  sumNotEvaluated?: number;
  sumCorrectAfterCorrection?: number;
  sumCorrectWithAdjustment?: number;
  sumUnsolvable?: number;
}

export interface EvaluationRates {
  /** Rated cases the extraction can be measured against (everything rated minus "nicht lösbar"). */
  attributable: number;
  /** Went through untouched. Ratio 0..1, `null` when there is nothing to measure. */
  straightThrough: number | null;
  /** The extraction itself was right — whether or not a business adjustment followed. */
  extractionCorrect: number | null;
  /** The final offer was right, however much review it took. */
  correctAfterReview: number | null;
}

/**
 * Quality rates over the counters above.
 *
 * The denominator is the *attributable* base: everything already rated, minus the cases the
 * pipeline could never have gotten right because the inquiry itself carries the error
 * (UNSOLVABLE_INQUIRY). Counting those would push every rate down for reasons outside the
 * extraction's control — the same reason unrated cases are excluded.
 *
 * "Korrekt, fachlich angepasst" counts as a correct extraction (`extractionCorrect`) but not as
 * straight-through: the extraction read the inquiry right, a human still had to touch the offer.
 */
export const evaluationRates = (counters: EvaluationCounters): EvaluationRates => {
  const rated = (counters.sumExtractions ?? 0) - (counters.sumNotEvaluated ?? 0);
  const attributable = Math.max(0, rated - (counters.sumUnsolvable ?? 0));
  const ratio = (n: number) => (attributable > 0 ? n / attributable : null);

  const correct = counters.sumCorrect ?? 0;
  const adjusted = counters.sumCorrectWithAdjustment ?? 0;
  return {
    attributable,
    straightThrough: ratio(correct),
    extractionCorrect: ratio(correct + adjusted),
    correctAfterReview: ratio(correct + adjusted + (counters.sumCorrectAfterCorrection ?? 0)),
  };
};

/** Renders a rate from `evaluationRates` for display; an unmeasurable rate shows as an en dash. */
export const formatRate = (rate: number | null): string => (rate === null ? '–' : `${(rate * 100).toFixed(1)}%`);
